// Shared projects' people in the pages (W9c): a member's colour as a dot, and a line of who added how many words
// (History's summary and each changed scene). Colours are set through the CSSOM (the page's CSP has no inline styles).
import { formatNumber } from './helpers.ts';
import { colorCSS, madeBy, membersOf } from '../store/members.ts';
import type { LiveProject } from '../store/project.ts';

export function PersonDot({ color, className = '' }: { color: string; className?: string }) {
  return <span className={`person-dot ${className}`.trim()} aria-hidden="true" ref={(el) => el?.style.setProperty('--person', colorCSS(color))} />;
}

const words = (n: number) => `${formatNumber(n)} ${n === 1 ? 'word' : 'words'}`;

/** "Anna added 312 words · Ben added 40 words", each with their dot. */
export function AuthorsLine({ people, className = 'diff-authors' }: { people: { id: string | null; name: string; color: string; words: number }[]; className?: string }) {
  if (!people.length) return null;
  return (
    <p className={className} data-diff-authors>
      {people.map((p, i) => (
        <span key={p.id ?? 'unknown'} className="author-added" data-author-name={p.name} data-author-words={p.words}>
          {i > 0 ? <span className="sep" aria-hidden="true"> · </span> : null}
          <PersonDot color={p.color} />
          {p.name} added {words(p.words)}
        </span>
      ))}
    </p>
  );
}

/** A comment's, reply's or suggestion's author: in a shared project the member whose device made it, in their colour
 *  (W9c); else the name it was signed with. */
export function CommentAuthor({ live, fragmentId, kind, id, signed }: {
  live: Pick<LiveProject, 'doc'> | null | undefined; fragmentId: string; kind: 'comment' | 'suggestion' | 'synopsis'; id: string; signed: string;
}) {
  const members = membersOf(live);
  const key = kind === 'suggestion' ? 'suggestions' : kind === 'synopsis' ? 'synopsisComments' : 'comments';
  const by = members ? madeBy(members, live?.doc(`fragment.${fragmentId}`), [key, id]) : null;
  if (!by?.known) return <span className="comment-author">{signed || 'Someone'}</span>;
  return (
    <span className="comment-author" data-member-color={by.color}>
      <PersonDot color={by.color} />
      {by.name}
    </span>
  );
}
